/**
 * Gestion du consentement sur la page `/cookies` (DECISIONS §11).
 *
 * Le choix est **modifiable à tout moment** : la page affiche l’état courant, puis le même panneau
 * que la bannière. Enregistrer applique immédiatement la décision (la mesure d’audience est créée
 * ou supprimée dans la foulée) et met à jour l’état affiché, sans rechargement.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  enregistrerChoixConsentement,
  useChoixConsentement,
  type ChoixConsentement,
} from '../cookies/consentement';
import { appliquerChoixMesure } from '../cookies/mesure-audience';
import { formaterDate } from '../i18n';
import { Bouton } from './Bouton';
import { PanneauTraceurs } from './PanneauTraceurs';

import type { TFunction } from 'i18next';

/** Texte de l’état courant, dans la langue affichée. */
function texteEtat(
  choix: ChoixConsentement | null,
  date: string,
  actif: string,
  inactif: string,
  t: TFunction,
): string {
  if (choix === null) return t('cookies.etat.aucun');
  if (choix.origine === 'tout-accepter') return t('cookies.etat.accepte', { date });
  if (choix.origine === 'refuser') return t('cookies.etat.refuse', { date });
  return t('cookies.etat.personnalise', {
    date,
    etat: choix.mesureAudience ? actif : inactif,
  });
}

export function GestionCookies() {
  const { t, i18n } = useTranslation();
  const choix = useChoixConsentement();
  const [ouvert, setOuvert] = useState(choix === null);
  const [mesure, setMesure] = useState(choix?.mesureAudience ?? false);

  useEffect(() => {
    appliquerChoixMesure(choix?.mesureAudience ?? false);
  }, [choix]);

  const date = choix === null ? '' : formaterDate(choix.date, i18n.language);
  const actif = t('cookies.etat.actif');
  const inactif = t('cookies.etat.inactif');

  return (
    <section className="gestion-cookies" aria-labelledby="gestion-cookies-titre" data-gestion-cookies="oui">
      <h2 className="gestion-cookies__titre" id="gestion-cookies-titre">
        {t('cookies.page.titre')}
      </h2>
      <p className="gestion-cookies__aide">{t('cookies.page.aide')}</p>
      <p
        className="gestion-cookies__etat"
        role="status"
        data-consentement={choix?.origine ?? 'aucun'}
      >
        {texteEtat(choix, date, actif, inactif, t)}
      </p>
      {ouvert ? (
        <PanneauTraceurs
          mesure={mesure}
          onChangerMesure={setMesure}
          onEnregistrer={() => {
            enregistrerChoixConsentement(mesure, 'personnaliser');
            appliquerChoixMesure(mesure);
            setOuvert(false);
          }}
        />
      ) : (
        <Bouton
          variante="secondaire"
          onClick={() => {
            setMesure(choix?.mesureAudience ?? false);
            setOuvert(true);
          }}
        >
          {t('cookies.etat.modifie')}
        </Bouton>
      )}
    </section>
  );
}
